import { getAppRuntimeSnapshot, hasReloadBlockingApiRequests, subscribeApiRequestsCompleted, subscribeAppRuntime } from "./appRuntimeGate";

const DEDUPE_MARKER = "1";

export type SharedAppReloadReason =
  | "auth-context-changed"
  | "chunk-load-failed"
  | "manual"
  | "startup-recovered"
  | "tenant-data-replaced"
  | "ui-build-changed";

export type SharedAppReloadRequest = {
  productKey: string;
  reason: SharedAppReloadReason;
  dedupeKey?: string | null;
  delayMs?: number;
  sessionStorageKey?: string;
  sessionStorageValue?: string;
};

const DEFAULT_RELOAD_DELAY_MS = 0;

function normalizeToken(value: string | null | undefined): string {
  return String(value ?? "").trim();
}

function getDedupeStorageKey(productKey: string, reason: SharedAppReloadReason, dedupeKey: string): string {
  return `${productKey}.appReload.${reason}.${dedupeKey}`;
}

function wasAlreadyHandled(req: SharedAppReloadRequest): boolean {
  const storageValue = normalizeToken(req.sessionStorageValue);
  if (req.sessionStorageKey && storageValue) {
    try {
      return window.sessionStorage.getItem(req.sessionStorageKey) === storageValue;
    } catch {
      return false;
    }
  }

  const dedupeKey = normalizeToken(req.dedupeKey);
  if (!dedupeKey) return false;
  try {
    return window.sessionStorage.getItem(getDedupeStorageKey(req.productKey, req.reason, dedupeKey)) === DEDUPE_MARKER;
  } catch {
    return false;
  }
}

function rememberHandled(req: SharedAppReloadRequest): void {
  const storageValue = normalizeToken(req.sessionStorageValue);
  try {
    if (req.sessionStorageKey && storageValue) {
      window.sessionStorage.setItem(req.sessionStorageKey, storageValue);
      return;
    }

    const dedupeKey = normalizeToken(req.dedupeKey);
    if (dedupeKey) {
      window.sessionStorage.setItem(getDedupeStorageKey(req.productKey, req.reason, dedupeKey), "1");
    }
  } catch {
    // If sessionStorage is unavailable, a single reload is still the safer outcome.
  }
}

let reloadScheduled = false;

export function requestSharedAppReload(req: SharedAppReloadRequest): boolean {
  if (typeof window === "undefined") return false;
  if (reloadScheduled) return true;
  if (wasAlreadyHandled(req)) return false;

  reloadScheduled = true;

  const delay = Math.max(0, req.delayMs ?? DEFAULT_RELOAD_DELAY_MS);
  let unsubscribeRuntime: (() => void) | undefined;
  let unsubscribeRequests: (() => void) | undefined;
  let finished = false;
  const reload = () => {
    if (finished) return;
    const protectedReload = req.reason === "ui-build-changed" || req.reason === "tenant-data-replaced" || req.reason === "startup-recovered";
    // Ein bestätigter UI-Wechsel ersetzt auch einen Client mit abgelösten
    // Kontrollpfaden. Die neue UI besitzt wieder den aktuellen Startvertrag.
    const waitsForRuntime = req.reason === "tenant-data-replaced" || req.reason === "startup-recovered";
    if ((protectedReload && hasReloadBlockingApiRequests()) || (waitsForRuntime && getAppRuntimeSnapshot().phase === "blocked")) {
      unsubscribeRuntime ??= subscribeAppRuntime(reload);
      unsubscribeRequests ??= subscribeApiRequestsCompleted(reload);
      return;
    }
    finished = true;
    unsubscribeRuntime?.();
    unsubscribeRequests?.();
    rememberHandled(req);
    window.location.reload();
  };
  // Ein bestätigter Build benötigt keine weitere Browser-Timer-Runde im Hintergrund.
  if (req.reason === "ui-build-changed" && delay === 0) queueMicrotask(reload);
  else window.setTimeout(reload, delay);
  return true;
}
